import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useChatStore } from "../store/useChatStore";
import "./NotesPage.css";
import DepartmentMasterModal from "./DepartmentMasterModal";

const API = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8080" : "")
).replace(/\/+$/, "");

const authHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const NotesPage = () => {
  const navigate = useNavigate();

  // SAME CATEGORY SOURCE AS CategorySidebar
  const { categories, getCategoryList } = useChatStore();

  const [notes, setNotes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [canManageDepartments, setCanManageDepartments] = useState(false);
  const [showDepartmentMaster, setShowDepartmentMaster] = useState(false);

  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    department: "",
    category: "",
    project: "",
  });

  useEffect(() => {
    if (typeof getCategoryList === "function") {
      getCategoryList();
    }
  }, [getCategoryList]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setLoadError("");

    // Load independently: a broken masters route must not hide existing notes.
    const [notesResult, mastersResult, departmentsResult] =
      await Promise.allSettled([
        axios.get(`${API}/api/notes`, { headers: authHeaders() }),
        axios.get(`${API}/api/notes/masters`, { headers: authHeaders() }),
        axios.get(`${API}/api/notes/departments`, { headers: authHeaders() }),
      ]);

    if (notesResult.status === "fulfilled") {
      const payload = notesResult.value.data;
      const notesList = Array.isArray(payload?.notes)
        ? payload.notes
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : null;

      if (notesList) {
        setNotes(notesList);
      } else {
        console.error("Unexpected Notes response:", payload);
        setLoadError("Notes API did not return a notes array.");
      }
    } else {
      const error = notesResult.reason;
      console.error(
        "Notes API error:",
        error.response?.status,
        error.response?.data,
      );
      setLoadError(
        error.response?.data?.message ||
          error.message ||
          "Unable to load notes.",
      );
    }

    if (mastersResult.status === "fulfilled") {
      const masters = mastersResult.value.data || {};
      setProjects(Array.isArray(masters.projects) ? masters.projects : []);
      setDepartments(
        Array.isArray(masters.departments) ? masters.departments : [],
      );
      setCanManageDepartments(masters.canManageDepartments === true);
    } else {
      console.error(
        "Notes masters API error:",
        mastersResult.reason.response?.status,
        mastersResult.reason.response?.data,
      );
    }

    // Department endpoint provides the authoritative Notes-only list and permission.
    if (departmentsResult.status === "fulfilled") {
      const data = departmentsResult.value.data || {};
      const list = Array.isArray(data.departments) ? data.departments : [];
      setDepartments(list.filter((item) => item.isActive !== false));
      setCanManageDepartments(data.canManageDepartments === true);
    } else {
      console.error(
        "Department Master API error:",
        departmentsResult.reason.response?.status,
        departmentsResult.reason.response?.data,
      );
    }

    setLoading(false);
  };

  const categoryOptions = useMemo(() => {
    return (Array.isArray(categories) ? categories : []).map((item) => ({
      value: String(item.category || item.code || item.name || "").trim(),

      label: item.name || item.category || item.code || "",
    }));
  }, [categories]);

  const filteredNotes = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return (Array.isArray(notes) ? notes : []).filter((note) => {
      const departmentName = note.department?.name || note.departmentName || "";

      const categoryName = note.category || "";

      const projectName = note.project?.name || "";

      const matchesSearch =
        !keyword ||
        String(note.name || "")
          .toLowerCase()
          .includes(keyword) ||
        String(departmentName).toLowerCase().includes(keyword) ||
        String(categoryName).toLowerCase().includes(keyword) ||
        String(projectName).toLowerCase().includes(keyword);

      const matchesDepartment =
        !departmentFilter ||
        String(note.department?._id || note.department) === departmentFilter;

      const matchesCategory =
        !categoryFilter || categoryName === categoryFilter;

      return matchesSearch && matchesDepartment && matchesCategory;
    });
  }, [notes, search, departmentFilter, categoryFilter]);

  const openCreateModal = () => {
    setForm({
      name: "",
      department: "",
      category: "",
      project: "",
    });

    setShowCreate(true);
  };

  const createNote = async () => {
    if (!form.name.trim()) {
      alert("Please enter note name.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      if (!token) {
        alert("Login session not found. Please login again.");
        return;
      }

      const response = await axios.post(
        `${API}/api/notes`,
        {
          name: form.name.trim(),
          department: form.department || null,
          category: form.category || "",
          project: form.project || null,
          blocks: [],
        },
        {
          headers: authHeaders(),
        },
      );

      const note = response.data?.note || response.data?.data || response.data;

      setShowCreate(false);

      if (note?._id) {
        navigate(`/tools/notes/${note._id}`);
      } else {
        await loadData();
      }
    } catch (error) {
      console.error("Create note error:", error);

      alert(error.response?.data?.message || "Unable to create note.");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (event, note) => {
    event.stopPropagation();

    if (!window.confirm(`Delete "${note.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/notes/${note._id}`, {
        headers: authHeaders(),
      });

      setNotes((previous) =>
        (Array.isArray(previous) ? previous : []).filter(
          (item) => item._id !== note._id,
        ),
      );
    } catch (error) {
      console.error("Delete note error:", error);

      alert("Unable to delete note.");
    }
  };

  return (
    <div className="notes-dashboard">
      <div className="notes-dashboard-header">
        <div>
          <h1>Notes</h1>

          <p>Create and organize project and department notes.</p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="ndm-master-button"
            onClick={() => {
              if (canManageDepartments) {
                setShowDepartmentMaster(true);
              } else {
                alert(
                  "Department Master is visible, but the backend has not granted this login admin access. " +
                    "Check GET /api/notes/departments → canManageDepartments and the logged-in user's role.",
                );
              }
            }}
          >
            ⚙ Department Master
          </button>
          <button className="notes-add-button" onClick={openCreateModal}>
            <span>＋</span>
            Add Note
          </button>
        </div>
      </div>

      <div className="notes-filter-card">
        <div className="notes-search">
          <span>⌕</span>

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search notes..."
          />
        </div>

        <select
          value={departmentFilter}
          onChange={(event) => setDepartmentFilter(event.target.value)}
        >
          <option value="">All Departments</option>

          {departments.map((department) => (
            <option key={department._id} value={department._id}>
              {department.name}
            </option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="">All Categories</option>

          {categoryOptions.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="notes-empty">Loading notes...</div>
      ) : loadError ? (
        <div className="notes-empty" role="alert">
          <h2>Unable to load notes</h2>
          <p>{loadError}</p>
          <button className="notes-add-button" type="button" onClick={loadData}>
            Retry
          </button>
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="notes-empty">
          <div className="notes-empty-icon">📝</div>

          <h2>No notes yet</h2>

          <p>Create your first note to start organizing information.</p>

          <button className="notes-add-button" onClick={openCreateModal}>
            ＋ Add Note
          </button>
        </div>
      ) : (
        <div className="notes-card-grid">
          {filteredNotes.map((note) => {
            const departmentName =
              note.department?.name || note.departmentName || "—";

            return (
              <div
                key={note._id}
                className="note-dashboard-card"
                onClick={() => navigate(`/tools/notes/${note._id}`)}
              >
                <div className="note-card-top">
                  <div className="note-card-icon">📝</div>

                  <button
                    className="note-card-delete"
                    onClick={(event) => deleteNote(event, note)}
                  >
                    ⋮
                  </button>
                </div>

                <h3>{note.name}</h3>

                <div className="note-meta">
                  <span>🏢 {departmentName}</span>

                  <span>🗂 {note.category || "—"}</span>
                </div>

                {note.project?.name && (
                  <div className="note-project">📁 {note.project.name}</div>
                )}

                <div className="note-mini-blocks">
                  {(note.blocks || []).slice(0, 4).map((block) => (
                    <span key={block._id}>
                      {block.type === "paragraph" && "¶"}
                      {block.type === "sketch" && "✏"}
                      {block.type === "points" && "•"}
                      {block.type === "image" && "▧"}
                      {block.type === "document" && "📄"}
                      {block.type === "link" && "🔗"}
                      {block.type === "subheading" && "H"}
                      {block.type === "checklist" && "✓"}
                      {block.type === "template" && "▦"}
                    </span>
                  ))}

                  {(note.blocks?.length || 0) > 4 && (
                    <span>+{note.blocks.length - 4}</span>
                  )}
                </div>

                <div className="note-card-footer">
                  <span>{note.blocks?.length || 0} components</span>

                  <span>
                    {note.updatedAt
                      ? new Date(note.updatedAt).toLocaleDateString()
                      : ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDepartmentMaster && canManageDepartments && (
        <DepartmentMasterModal
          onClose={() => setShowDepartmentMaster(false)}
          onChanged={loadData}
        />
      )}

      {/* CREATE NOTE MODAL */}

      {showCreate && (
        <div
          className="note-modal-overlay"
          onMouseDown={() => setShowCreate(false)}
        >
          <div
            className="note-create-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="note-modal-header">
              <div>
                <h2>Create New Note</h2>

                <p>Select department, category and project.</p>
              </div>

              <button onClick={() => setShowCreate(false)}>×</button>
            </div>

            <div className="note-modal-body">
              <label>Note Name *</label>

              <input
                value={form.name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    name: event.target.value,
                  })
                }
                placeholder="Enter note name"
                autoFocus
              />

              <label>Department</label>

              <select
                value={form.department}
                onChange={(event) =>
                  setForm({
                    ...form,
                    department: event.target.value,
                  })
                }
              >
                <option value="">Select Department</option>

                {departments.map((department) => (
                  <option key={department._id} value={department._id}>
                    {department.name}
                  </option>
                ))}
              </select>

              <label>Category</label>

              <select
                value={form.category}
                onChange={(event) =>
                  setForm({
                    ...form,
                    category: event.target.value,
                  })
                }
              >
                <option value="">Select Category</option>

                {categoryOptions.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              <label>Project</label>

              <select
                value={form.project}
                onChange={(event) =>
                  setForm({
                    ...form,
                    project: event.target.value,
                  })
                }
              >
                <option value="">Select Project</option>

                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="note-modal-footer">
              <button
                className="note-cancel-button"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>

              <button
                className="notes-add-button"
                onClick={createNote}
                disabled={saving}
              >
                {saving ? "Creating..." : "Create Note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;
